import {
  DialogDescription,
  CurrencyFormatted,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
  Typography,
  Dialog,
  Button,
  Anchor,
} from '@ui/components';
import { DashboardAccountResponse } from '@interfaces/models';
import { useGetDashboard } from '@pages/dashboard/hooks';
import { DataTransfer } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';
import { useState } from 'react';
import { cn } from '@ui/utils';

import {
  DAYS_LEFT_WARNING_THRESHOLD,
  BALANCE_CRITICAL_THRESHOLD,
} from '../dashboard-wallet/consts';

interface DashboardAccountsDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onTopUpClick: () => void;
}

export const DashboardAccountsDialog = (props: DashboardAccountsDialogProps) => {
  const { isOpen, setIsOpen } = props;
  const { currency } = useCurrentUser();
  const { t } = useTranslation();

  const { data } = useGetDashboard();

  const [selectedAccount, setSelectedAccount] = useState<DashboardAccountResponse | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedAccount(null);
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('DASHBOARD.WALLET.TOP_UP_DIALOG.TITLE')}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-body-default">
          {t('DASHBOARD.WALLET.TOP_UP_DIALOG.DESCRIPTION')}
        </DialogDescription>

        <div className="flex flex-col gap-2 p-4">
          {data?.accounts?.map((account) => (
            <div
              className={cn(
                'flex px-3 py-4 border border-border-default rounded-md gap-3 items-center cursor-pointer',
                {
                  'border-surface': selectedAccount?.iban === account.iban,
                },
              )}
              onClick={() => setSelectedAccount(account)}
              key={account.iban}
            >
              <div className="w-12 h-8 rounded-xs flex items-center justify-center border border-border-default">
                <DataTransfer />
              </div>

              <div className="flex flex-col gap-1">
                <Typography variant="h100">{account.iban}</Typography>
                <Typography variant="labelSmallBook" className="text-weak">
                  {t('DASHBOARD.WALLET.TOP_UP_DIALOG.BALANCE')}:{' '}
                  <CurrencyFormatted
                    className={cn('text-weak', {
                      'text-warning': (account.daysLeft ?? Infinity) <= DAYS_LEFT_WARNING_THRESHOLD,
                      'text-critical': account.balance === BALANCE_CRITICAL_THRESHOLD,
                    })}
                    value={account.balance!}
                    currency={currency}
                  />
                </Typography>
                <Typography variant="labelSmallBook" className="text-weak">
                  {t('DASHBOARD.WALLET.TOP_UP_DIALOG.CONST_CENTER')}:{' '}
                  {account.costCenters.map((center) => center.name).join(', ')}
                </Typography>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="tertiary" type="button" size="large" wide>
              {t('DASHBOARD.WALLET.TOP_UP_DIALOG.CANCEL')}
            </Button>
          </DialogClose>
          <Anchor disabled={!selectedAccount} asChild>
            <Link
              // We want to redirect to the first const center of the selected account
              to={`../${Routes.COST_CENTER}/${selectedAccount?.costCenters[0]?.id}`}
              state={{ isTopUpDrawerOpen: true }}
              className="w-full"
            >
              <Button disabled={!selectedAccount} size="large" wide>
                {t('DASHBOARD.WALLET.TOP_UP_DIALOG.CONFIRM')}
              </Button>
            </Link>
          </Anchor>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
